// js/supabase.js
import { createClient } from '@supabase/supabase-js'
import config from './config.js'

// Create Supabase client using environment variables
export const supabase = createClient(
  config.supabase.url,
  config.supabase.anonKey
)

// Database service functions
export const dbService = {
  // Projects
  async getProjects() {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .order('created_at', { ascending: false })
    
    if (error) {
      console.error('Error fetching projects:', error)
      return []
    }
    return data || []
  },

  async getProjectsByCategory(category) {
    if (category === 'All Projects') {
      return this.getProjects()
    }
    
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('category', category)
      .order('created_at', { ascending: false })
    
    if (error) {
      console.error('Error fetching projects by category:', error)
      return []
    }
    return data || []
  },

  async searchProjects(searchTerm) {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .or(`name.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,founder.ilike.%${searchTerm}%`)
      .order('created_at', { ascending: false })
    
    if (error) {
      console.error('Error searching projects:', error)
      return []
    }
    return data || []
  },

  async incrementProjectViews(projectId) {
    const { error } = await supabase
      .rpc('increment_views', { project_id: projectId })
    
    if (error) {
      console.error('Error incrementing views:', error)
    }
  },

  // Tools
  async getTools() {
    const { data, error } = await supabase
      .from('tools')
      .select('*')
      .order('name')
    
    if (error) {
      console.error('Error fetching tools:', error)
      return []
    }
    return data || []
  },

  // Blog posts
  async getBlogPosts() {
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .not('published_at', 'is', null)
      .order('published_at', { ascending: false })
    
    if (error) {
      console.error('Error fetching blog posts:', error)
      return []
    }
    return data || []
  },

  async getBlogPostBySlug(slug) {
    const { data, error } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('slug', slug)
      .not('published_at', 'is', null)
      .single()
    
    if (error) {
      console.error('Error fetching blog post:', error)
      return null
    }
    return data
  },

  // Competitions
  async getCompetitions() {
    const { data, error } = await supabase
      .from('competitions')
      .select('*')
      .order('created_at', { ascending: false })
    
    if (error) {
      console.error('Error fetching competitions:', error)
      return []
    }
    return data || []
  },

  async getCompetitionsByStatus(status) {
    if (status === 'all') {
      return this.getCompetitions()
    }
    
    const { data, error } = await supabase
      .from('competitions')
      .select('*')
      .eq('status', status)
      .order('created_at', { ascending: false })
    
    if (error) {
      console.error('Error fetching competitions by status:', error)
      return []
    }
    return data || []
  },

  // Statistics
  async getProjectStats() {
    const projects = await this.getProjects()
    
    // Count tool usage
    const toolCount = {}
    projects.forEach(project => {
      project.technology.forEach(tech => {
        toolCount[tech] = (toolCount[tech] || 0) + 1
      })
    })

    // Find most used tool
    const mostUsedTool = Object.entries(toolCount)
      .sort((a, b) => b[1] - a[1])[0]

    return {
      totalProjects: projects.length,
      mostUsedTool: mostUsedTool ? `${mostUsedTool[0]} (${mostUsedTool[1]} projects)` : 'No data',
      toolCount
    }
  }
}

